import type AtlasMergePrepLogModel from '$shared/models/AtlasMergePrepLogModel';

const regex =
  /^Resolution ID:\s*[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export default class LogProcessor {
  readonly stateLogs: AtlasMergePrepLogModel;
  private logsWithoutEmptyList: Record<string, any[]> = {};
  private infoLogs: Record<string, any[]> = {};
  private warningLogs: Record<string, any[]> = {};
  private errorLogs: Record<string, any[]> = {};
  private unknownLogs: Record<string, any[]> = {};
  readonly orderMap: Map<string, number>;

  constructor(logs: AtlasMergePrepLogModel) {
    this.stateLogs = logs;
    this.prepareLogs();
    this.orderMap = new Map(this.stateLogs.sortedKeys.map((value, index) => [value, index]));
  }

  getAllLogs(): Record<string, any[]> {
    return this.logsWithoutEmptyList;
  }

  getErrorLogs(): Record<string, any[]> {
    return this.errorLogs;
  }

  /**
   * Merges error and warning logs into a single object, prioritizing error logs.
   *
   * Iterates over the unique keys from both `errorLogs` and `warningLogs` objects,
   * and combines the corresponding arrays for each key, placing error logs before warning logs.
   *
   * @returns {Record<string, any[]>} An object where each key maps to an array containing
   * both error and warning logs, with error logs appearing first.
   */
  getPriorityLogs(): Record<string, any[]> {
    const merged: Record<string, any[]> = {};
    for (const key of new Set([...Object.keys(this.errorLogs), ...Object.keys(this.warningLogs)])) {
      merged[key] = [...(this.errorLogs[key] || []), ...(this.warningLogs[key] || [])];
    }
    return merged;
  }

  /**
   * Returns the keys of the provided logs object, sorted according to the order specified in `orderMap`.
   * orderMap is prepared when the instance is created based on teh sortOrder from AtlasMergePrepStateModel
   * Keys not present in `orderMap` are placed at the end of the sorted array.
   *
   * @param logs - An object containing log entries, where each key is a log identifier.
   * @returns An array of keys sorted based on their order in `orderMap`.
   */
  getSortedKeys(logs: Record<string, any>): string[] {
    return Object.keys(logs).sort((a, b) => {
      return (this.orderMap.get(a) ?? Infinity) - (this.orderMap.get(b) ?? Infinity);
    });
  }

  /**
   * Processes the logs stored in `this.stateLogs.logs` by categorizing them based on their log level.
   *
   * - Iterates over each key in the logs object.
   * - If the log list for a key is not empty, it is added to `this.logsWithoutEmptyList`.
   * - Determines the log level (`info`, `warning`, `error`, or unknown) for each log entry and
   *   assigns the log to the corresponding property: `this.infoLogs`, `this.warningLogs`, `this.errorLogs`, or `this.unknownLogs`.
   *
   * Assumes that each log entry under a key has a `level` property.
   */
  private prepareLogs() {
    Object.keys(this.stateLogs.logs).forEach((key) => {
      this.updateResolutionIdAndFilterLogs(key);
      if (this.stateLogs.logs[key].length > 0) {
        this.logsWithoutEmptyList[key] = this.stateLogs.logs[key];
        for (const log of this.logsWithoutEmptyList[key]) {
          const logLevel = log.level?.toLowerCase();
          let logMap: Record<string, any[]> | undefined;
          if (logLevel === 'info') {
            logMap = this.infoLogs;
          } else if (logLevel === 'warning') {
            logMap = this.warningLogs;
          } else if (logLevel === 'error') {
            logMap = this.errorLogs;
          } else {
            logMap = this.unknownLogs;
          }
          if (!logMap[key]) {
            logMap[key] = [];
          }
          logMap[key].push(log);
        }
      }
    });
  }

  private updateResolutionIdAndFilterLogs(key: string): void {
    const logs = this.stateLogs.logs[key];
    if (!logs || logs.length === 0) {
      return;
    }
    for (const log of logs) {
      if (log.hasOwnProperty('resolutionId')) {
        return;
      }
      const details = log.details;
      const resolutionIdLog = details?.find((l) => regex.test(l.check));
      const resolutionId = resolutionIdLog?.check.replace('Resolution ID:', '').trim();
      log.resolutionId = resolutionId;
      log.details = details?.filter((l) => !regex.test(l.check));
    }
  }
}
